import { FormSendResult } from "client/openapi";
import { Button, ButtonColor, ButtonSize } from "components/Button";
import { Dialog, DialogContent } from "components/Dialog";
import {
  errorText,
  sendFormInBatches,
  sendSummary,
} from "components/Forms/formsApi";
import { ChannelChecks, Tick } from "components/Forms/SendFormControls";
import { useState } from "react";
import Notifications from "util/notifications";
import { OutstandingForm, ROLE_OF, nameAndRole } from "./shared";

/** One form not filled out yet, and each open link of it (one per person). */
export type FormGroup = {
  formId: number;
  formName: string;
  sends: OutstandingForm[];
};

/**
 * Resend a form: says which form goes to whom, then how (only the channels
 * they have; SMS first, as everywhere). Sent to more than one person, each
 * is ticked and can be left out. Each gets their same link again, working
 * two more weeks; onSent gets the links that went out.
 */
export default function ResendFormDialog({
  orgId,
  group,
  onSent,
  onClose,
}: {
  orgId: number;
  group: FormGroup;
  onSent: (sendIds: number[]) => void;
  onClose: () => void;
}) {
  const [picked, setPicked] = useState(group.sends.map((s) => s.send_id));
  const chosen = group.sends.filter((s) => picked.includes(s.send_id));
  const show = {
    sms: chosen.some((s) => s.can_text !== false),
    email: chosen.some((s) => s.can_email !== false),
  };
  const [channels, setChannels] = useState(() => {
    const sms = group.sends.some((s) => s.can_text !== false);
    return { sms, email: !sms };
  });
  const use = {
    sms: channels.sms && show.sms,
    email: channels.email && show.email,
  };
  const [sending, setSending] = useState(false);
  const one = group.sends.length === 1;

  async function send() {
    setSending(true);
    try {
      const results = await sendFormInBatches({
        orgId,
        formId: group.formId,
        ...use,
        recipients: chosen.map((s) => ({
          person_type: ROLE_OF[s.sent_to.kind],
          person_id: s.sent_to.id,
        })),
      });
      const went = (s: OutstandingForm) =>
        results.some(
          (r) =>
            r.status === FormSendResult.status.SENT &&
            r.person_type === ROLE_OF[s.sent_to.kind] &&
            r.person_id === s.sent_to.id
        );
      const sent = chosen.filter(went);
      const summary = sendSummary(results);
      if (sent.length === chosen.length) Notifications.success(summary);
      else Notifications.error(summary);
      if (sent.length > 0) onSent(sent.map((s) => s.send_id));
      if (sent.length === chosen.length) onClose();
    } catch (e) {
      Notifications.error(errorText(e, "The form could not be sent."));
    } finally {
      setSending(false);
    }
  }

  const blocked = sending || chosen.length === 0 || (!use.sms && !use.email);

  return (
    <Dialog open onOpenChange={(open) => !open && !sending && onClose()}>
      <DialogContent className="dialog-content !max-w-[440px]">
        <h2 className="pr-8 text-lg font-semibold text-gray-900">
          Resend {group.formName}
        </h2>
        {one ? (
          <p className="mt-2 text-sm text-gray-700">
            {nameAndRole(group.sends[0].sent_to)} gets the same link again. It
            works for two more weeks.
          </p>
        ) : (
          <>
            <p className="mt-2 text-sm text-gray-700">
              Send the same link again to these people. Each link works for two
              more weeks.
            </p>
            <div className="mt-3 flex flex-col gap-2 text-sm text-gray-900">
              {group.sends.map((s) => (
                <Tick
                  key={s.send_id}
                  checked={picked.includes(s.send_id)}
                  onChange={(checked) =>
                    setPicked((prev) =>
                      checked
                        ? [...prev, s.send_id]
                        : prev.filter((id) => id !== s.send_id)
                    )
                  }
                  disabled={sending}
                >
                  {nameAndRole(s.sent_to)}
                </Tick>
              ))}
            </div>
          </>
        )}
        <div className="mt-4 border-t border-gray-100 pt-4">
          {show.sms || show.email ? (
            <div className="flex items-center gap-4 text-sm">
              <span className="text-gray-600">Send by</span>
              <ChannelChecks
                sms={channels.sms}
                email={channels.email}
                onChange={setChannels}
                disabled={sending}
                show={show}
              />
            </div>
          ) : (
            <p className="text-sm text-gray-600">
              {chosen.length === 0
                ? "Tick who gets it."
                : "No phone number or email is on file. Add one on their page to resend."}
            </p>
          )}
        </div>
        <div className="mt-5 flex justify-end">
          <Button
            color={blocked ? ButtonColor.GRAY : ButtonColor.GREEN}
            size={ButtonSize.SMALL}
            disabled={blocked}
            onClick={send}
          >
            {sending ? "Sending..." : "Send"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
